"use client";

import { useState, useEffect, useMemo } from "react";
import { X, ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { resolveContentImageUrl } from "@/lib/r2-url";
import {
  useAnnouncement,
  useAnnouncementSession,
  hashCode,
  isAnnouncementDismissed,
  type AnnouncementData,
} from "@/stores/announcement-store";

/** Server-provided announcement (subset of the R2 document) — passed from
 *  the (main) layout through Header so the banner is part of the SSR HTML. */
export interface AnnouncementInitial {
  text: string;
  link?: string;
  ctaLabel?: string;
  dismissible?: boolean;
  theme?: AnnouncementData["theme"];
  bgColor?: string;
  textColor?: string;
  align?: AnnouncementData["align"];
  icon?: string;
  imageKey?: string;
  showDialog?: boolean;
}

/** Bar background/foreground for each built-in theme. `custom` uses inline colours. */
function barAppearance(data: AnnouncementData): { className: string; style?: React.CSSProperties } {
  if (data.theme === "custom" && (data.bgColor || data.textColor)) {
    return {
      className: "",
      style: { backgroundColor: data.bgColor || "var(--primary)", color: data.textColor || "#ffffff" },
    };
  }
  switch (data.theme) {
    case "cream":
      return { className: "bg-mist text-foreground" };
    case "dark":
      return { className: "bg-foreground text-background" };
    case "sage":
    default:
      return { className: "bg-primary text-primary-foreground" };
  }
}

export function AnnouncementBar({
  initial,
}: {
  initial?: AnnouncementInitial | null;
}) {
  const storeData = useAnnouncement((s) => s.data);
  const dismissedHash = useAnnouncement((s) => s.dismissedHash);
  const setData = useAnnouncement((s) => s.setData);
  const dismissStore = useAnnouncement((s) => s.dismiss);
  const dialogSeenHash = useAnnouncementSession((s) => s.dialogSeenHash);
  const markDialogSeen = useAnnouncementSession((s) => s.markDialogSeen);

  // The server-read announcement keeps the banner in the SSR HTML; the
  // client fetch below refreshes/overrides it via the store. Falling back
  // to the prop until then removes the post-hydration banner injection
  // (CLS ~0.06–0.12 whenever a banner was active — 2026-06-11 audit).
  const initialData = useMemo<AnnouncementData | null>(
    () =>
      initial?.text
        ? {
            text: initial.text,
            link: initial.link,
            ctaLabel: initial.ctaLabel,
            active: true,
            dismissible: initial.dismissible,
            theme: initial.theme,
            bgColor: initial.bgColor,
            textColor: initial.textColor,
            align: initial.align,
            icon: initial.icon,
            imageKey: initial.imageKey,
            // The layout only passes `initial` when the bar should show.
            showBar: true,
            showDialog: initial.showDialog,
          }
        : null,
    [initial]
  );
  const data = storeData ?? initialData;

  // DERIVED, never stored. The store keeps only `dismissedHash`; comparing it
  // against the announcement currently in view is what makes a content change
  // re-show the bar, and it is why a persisted "dismissed: true" flag can no
  // longer hide the bar forever. Works for the server-provided `initial` prop
  // too, which the old localStorage read had to special-case.
  const dismissed = isAnnouncementDismissed(dismissedHash, data);

  // Dismissal is persisted — unknowable on the server. Render the banner until
  // hydration confirms `dismissed`, so the SSR and first client render agree; a
  // previously-dismissed visitor sees one banner collapse, the trade for a
  // shift-free first paint for everyone else.
  const [hydrated, setHydrated] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- storage-backed dismissal state is intentionally read only after hydration.
    setHydrated(true);
  }, []);

  const showBar = data ? data.showBar !== false : false;
  const showDialog = data ? data.showDialog !== false : false;

  // NOTE: this component does NOT measure itself into `--banner-h` any more.
  // `HeaderBanners` measures the whole banner stack (this bar plus the closure
  // notice) as one wrapper and owns that variable outright. Measuring only this
  // bar under-reported the slot the moment a second bar joined it, and the old
  // "collapsed → set 0px" branch became an outright lie, since a closure bar can
  // still occupy the slot after this one is dismissed. Do not reintroduce a
  // writer here: two writers racing on one variable is a layout-shift bug.

  useEffect(() => {
    fetch("/api/announcement")
      .then((r) => r.json())
      .then((d: unknown) => {
        const ann = d as Partial<AnnouncementData> & { active?: boolean; text?: string };
        if (!ann?.active || !ann?.text) return;

        // No "reset the dismissal if the hash moved on" branch and no
        // localStorage probe: `dismissed` is derived from a comparison with THIS
        // announcement's hash, so both fall out of the derivation for free.
        setData({
          text: ann.text,
          link: ann.link,
          ctaLabel: ann.ctaLabel,
          active: true,
          dismissible: ann.dismissible,
          theme: ann.theme,
          bgColor: ann.bgColor,
          textColor: ann.textColor,
          align: ann.align,
          icon: ann.icon,
          imageKey: ann.imageKey,
          showBar: ann.showBar,
          showDialog: ann.showDialog,
        });
      })
      .catch((err) => console.error("[AnnouncementBar] Failed to fetch announcement:", err));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Show the dialog once per tab session for an active announcement (when
  // enabled). "Already seen" is the session store's `dialogSeenHash`, not a
  // hand-rolled `sessionStorage["ann-dialog-<hash>"]` — and not a local
  // `dialogShown` flag either, which was a second copy of the same fact.
  useEffect(() => {
    if (!data || dismissed || !showDialog) return;
    const hash = hashCode(data.text);
    if (dialogSeenHash === hash) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- opens the once-per-session browser dialog for the active announcement.
    setDialogOpen(true);
    markDialogSeen(hash);
  }, [data, dismissed, showDialog, dialogSeenHash, markDialogSeen]);

  if (!data || (hydrated && dismissed)) return null;

  const dismiss = () => {
    // One writer. The store's `persist` owns the localStorage round-trip; the
    // old hand-rolled `ann-dismissed-<hash>` key is gone.
    dismissStore(hashCode(data.text));
    setDialogOpen(false);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const appearance = barAppearance(data);
  const alignLeft = data.align === "left";
  const ctaLabel = data.ctaLabel?.trim() || "Learn more";
  const heroImage = resolveContentImageUrl(data.imageKey);

  return (
    <>
      {/* ─── Top banner ─── */}
      {/*
        Layout: the wrapper is a flex row so the dismiss button reserves
        its own space at the right (instead of being absolutely positioned
        and overlapping the trailing text on narrow phones).
      */}
      {showBar && (
        <>
          <div
            className={`relative flex w-full items-center gap-2 py-2.5 pl-4 pr-2 text-sm font-medium sm:pl-10 ${appearance.className}`}
            style={appearance.style}
          >
            {/* Spacer mirrors the dismiss button's width on the left so centred
                text reads visually centred. Hidden when there's no dismiss
                button or when the text is left-aligned. */}
            {data.dismissible !== false && !alignLeft && (
              <span className="block size-11 shrink-0 sm:hidden" aria-hidden="true" />
            )}
            <div
              className={`flex min-w-0 flex-1 items-center gap-1.5 ${
                alignLeft ? "justify-start text-left" : "justify-center text-center"
              }`}
            >
              {data.icon && (
                <span className="shrink-0 leading-none" aria-hidden="true">
                  {data.icon}
                </span>
              )}
              {data.link ? (
                <a
                  href={data.link}
                  className="block min-w-0 underline underline-offset-2 hover:opacity-90 leading-snug"
                >
                  {data.text}
                </a>
              ) : (
                <span className="block min-w-0 leading-snug">{data.text}</span>
              )}
            </div>
            {data.dismissible !== false && (
              <button
                onClick={dismiss}
                className="flex size-11 shrink-0 items-center justify-center rounded-full opacity-70 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-1"
                aria-label="Dismiss announcement"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* The pre-paint `--banner-h` script that used to sit here now lives
              in `HeaderBanners`, measuring the whole stack. It has to run even
              when this bar is absent but a closure notice is not, so it cannot
              stay inside this `showBar &&` branch. */}
        </>
      )}

      {/* ─── Dialog ─── */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <DialogContent
          showCloseButton={false}
          className="max-w-105 gap-0 overflow-hidden border-0 bg-background p-0 shadow-2xl sm:rounded-3xl"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Announcement</DialogTitle>
            <DialogDescription>Announcement from The Floral Muse</DialogDescription>
          </DialogHeader>

          {/* Optional hero image */}
          {heroImage && (
            // eslint-disable-next-line @next/next/no-img-element -- CMS image; may be a proxy URL next/image can't optimise
            <img
              src={heroImage}
              alt=""
              className="h-44 w-full object-cover"
            />
          )}

          {/* Header */}
          <div className={`relative px-10 pb-4 text-center ${heroImage ? "pt-6" : "bg-linear-to-b from-(--mist) to-background pt-10"}`}>
            <div className="mx-auto mb-5 h-px w-10 bg-(--sage)" />
            <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-(--stone)">
              The Floral Muse
            </p>
            <h2 className="mt-2 font-serif text-[1.65rem] font-semibold leading-snug text-foreground sm:text-[1.85rem]">
              A Little Note
            </h2>
          </div>

          {/* Content card */}
          <div className="px-8 pb-8 pt-2">
            <div className="relative rounded-2xl bg-(--mist) px-7 py-7">
              {/* Corner accents — top-left & bottom-right */}
              <div className="pointer-events-none absolute left-3 top-3 h-5 w-5">
                <div className="absolute left-0 top-0 h-full w-px bg-(--sage) opacity-40" />
                <div className="absolute left-0 top-0 h-px w-full bg-(--sage) opacity-40" />
              </div>
              <div className="pointer-events-none absolute bottom-3 right-3 h-5 w-5">
                <div className="absolute bottom-0 right-0 h-full w-px bg-(--sage) opacity-40" />
                <div className="absolute bottom-0 right-0 h-px w-full bg-(--sage) opacity-40" />
              </div>

              <p className="text-center font-serif text-lg leading-[1.75] text-foreground sm:text-xl">
                {data.text}
              </p>
            </div>

            {/* Actions */}
            <div className="mt-6 flex flex-col items-center gap-3">
              {data.link && (
                <Button asChild size="lg" className="w-full gap-2 rounded-xl">
                  <a href={data.link}>
                    {ctaLabel}
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </Button>
              )}
              <Button
                variant="outline"
                size="lg"
                onClick={closeDialog}
                className="w-full rounded-xl"
              >
                Continue browsing
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
